import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/roles";

const naira = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });
const STATUSES = ["pending", "approved", "rejected", "all"];
const BADGE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800" };

function Change({ from, to }) {
    if (to === null || to === undefined) return <span className="text-ink/40">No change</span>;
    return <span className="tabular-nums">{naira(from)} <span className="text-ink/40">→</span> <b>{naira(to)}</b></span>;
}

export default function PayChangeRequests() {
    const { user } = useAuth();
    const isAdmin = hasRole(user, "admin");
    const [status, setStatus] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/pay-change-requests", { params: { status } });
            setRows(data || []);
        } catch {
            toast.error("Could not load requests");
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const decide = async (r, action) => {
        const note = action === "reject" ? window.prompt("Reason for rejecting (optional)") : "";
        if (note === null) return;
        setBusy(r.id);
        try {
            await api.post(`/pay-change-requests/${r.id}/${action}`, { note: note || "" });
            toast.success(action === "approve" ? "Approved — staff pay updated" : "Request rejected");
            window.dispatchEvent(new Event("badges:refresh"));
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
        } finally {
            setBusy("");
        }
    };

    return (
        <div className="p-4 sm:p-8 lg:p-12 max-w-7xl" data-testid="pay-change-requests-page">
            <div className="overline">{isAdmin ? "Approvals" : "HR"}</div>
            <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink flex items-center gap-3">
                <BadgeDollarSign className="w-9 h-9 text-klein" /> Pay change requests
            </h1>
            <p className="mt-2 text-sm text-ink/60">
                {isAdmin ? "HR proposes salary or allowance changes here. Approving applies the change to the staff record." : "Requests you send from a staff profile wait here for Admin approval."}
            </p>

            <div className="mt-8 flex gap-1 border-b border-ink/10">
                {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(s)} className={`px-4 py-2.5 text-sm capitalize border-b-2 -mb-px ${status === s ? "border-klein text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"}`} data-testid={`pcr-tab-${s}`}>{s}</button>
                ))}
            </div>

            <div className="mt-6 bg-white border border-ink/10 overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]" data-testid="pcr-table">
                    <thead>
                        <tr className="border-b border-ink/10 bg-bone/50">
                            {["Staff", "Salary", "Other allowance", "Reason", "Requested", "Status", ""].map((h) => <th key={h} className="text-left overline px-3 py-3">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-ink/40">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-ink/40" data-testid="pcr-empty">No {status === "all" ? "" : status} requests</td></tr>}
                        {!loading && rows.map((r) => (
                            <tr key={r.id} className="border-b border-ink/5 align-top" data-testid={`pcr-row-${r.id}`}>
                                <td className="px-3 py-3">
                                    <Link to={`/employees/${r.employee_id}`} className="font-medium hover:text-klein">{r.employee_name}</Link>
                                    <div className="text-xs text-ink/50">{r.employee_no} · {r.department || "No department"}</div>
                                </td>
                                <td className="px-3 py-3"><Change from={r.current_salary} to={r.new_salary} /></td>
                                <td className="px-3 py-3"><Change from={r.current_other_allowance} to={r.new_other_allowance} /></td>
                                <td className="px-3 py-3 text-ink/70 max-w-[220px]">{r.reason}{r.effective_date && <div className="text-xs text-ink/40">Effective {r.effective_date}</div>}</td>
                                <td className="px-3 py-3 text-xs text-ink/60">{r.requested_by_name}<div>{new Date(r.created_at).toLocaleDateString()}</div></td>
                                <td className="px-3 py-3">
                                    <span className={`text-xs px-2 py-1 capitalize ${BADGE[r.status]}`} data-testid={`pcr-status-${r.id}`}>{r.status}</span>
                                    {r.decided_by_name && <div className="text-xs text-ink/40 mt-1">by {r.decided_by_name}{r.decision_note ? ` — ${r.decision_note}` : ""}</div>}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                    {isAdmin && r.status === "pending" && (
                                        <div className="flex gap-2">
                                            <button disabled={busy === r.id} onClick={() => decide(r, "approve")} className="inline-flex items-center gap-1 bg-emerald-700 text-white px-3 py-1.5 text-xs hover:bg-emerald-800 disabled:opacity-50" data-testid={`pcr-approve-${r.id}`}><Check className="w-3.5 h-3.5" /> Approve</button>
                                            <button disabled={busy === r.id} onClick={() => decide(r, "reject")} className="inline-flex items-center gap-1 border border-red-300 text-red-700 px-3 py-1.5 text-xs hover:bg-red-50 disabled:opacity-50" data-testid={`pcr-reject-${r.id}`}><X className="w-3.5 h-3.5" /> Reject</button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
